import React from "react";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { GraphConfig } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { IgStackedHorizBarLegend } from "@src/features/analytics/legends/IgStackedHorizBarLegend";
import { IgHorizStackedBarTooltip } from "@src/features/analytics/tooltips/IgHorizStackedBarTooltip";
import {
  getIgHorizStackedBarColor,
  getIgHorizStackedBarName,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { StackedBarMetricData } from "@src/types/analytics";
import { genericSortByOrder } from "@src/utils/sortings";

interface IgHorizStackedBarChartProps {
  config: GraphConfig;
  groupMetricData: StackedBarMetricData[];
  graphHeight?: number;
}

const StyledStackedBarChart = styled(BarChart)(({ theme }) => ({
  [`& .recharts-cartesian-axis-tick-value`]: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
  },
}));

export const IgHorizStackedBarChart = ({
  config,
  groupMetricData,
  graphHeight,
}: IgHorizStackedBarChartProps) => {
  const variations = useSelector(selectedVariations);
  const [hoveredCell, setHoveredCell] = useImmer("");
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height={graphHeight ?? 300}>
      <StyledStackedBarChart
        data={groupMetricData.sort(genericSortByOrder)}
        layout={"vertical"}
      >
        <XAxis
          type="number"
          domain={[0, 1]}
          tickFormatter={(value) => `${Math.round(value)}%`}
          tickLine={false}
          axisLine={false}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
        />
        <YAxis
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          type={"category"}
          dataKey={"variation_id"}
          tickLine={false}
          tickFormatter={(value: any) => {
            return (
              variations.find((variation) => variation.id === value)?.name ||
              value
            );
          }}
        />
        <Tooltip
          cursor={{ fill: "transparent" }}
          content={
            <IgHorizStackedBarTooltip
              groupMetricData={groupMetricData.sort(genericSortByOrder)}
              hoveredCell={hoveredCell}
            />
          }
        />
        <Legend
          content={<IgStackedHorizBarLegend />}
          wrapperStyle={{ marginLeft: "40px", paddingTop: "24px" }}
        />
        {Object.keys(groupMetricData[0].stackedHorizValues!)
          .filter(
            (category) =>
              category !== "N/A" && category !== "" && category !== "undefined"
          )
          .map((unit, index) => (
            <Bar
              key={unit}
              onMouseEnter={(data) => {
                if (data.tooltipPayload) {
                  setHoveredCell(data.tooltipPayload[0].name);
                }
              }}
              dataKey={(entry) => entry.stackedHorizValues[unit]}
              name={getIgHorizStackedBarName(unit, config)}
              stackId="a"
              fill={getIgHorizStackedBarColor(config, index, unit)}
            />
          ))}
      </StyledStackedBarChart>
    </ResponsiveContainer>
  );
};
